import React, { useEffect, useRef, useState } from "react";
import { FolderOpen, HardDriveDownload } from "lucide-react";
import { useI18n } from "@/lib/i18n";

const RAW = [
  { name: "DSC09781.CR3", lane: "edit" },
  { name: "DSC09789.CR3", lane: "print", size: "8x12in(SC)" },
  { name: "DSC09802.CR3", lane: "edit" },
  { name: "DSC09815.CR3", lane: "print", size: "12x18in(SC)+F" },
  { name: "DSC09827.CR3", lane: "edit" },
  { name: "DSC09833.CR3", lane: "print", size: "8x12in(SC)" },
];

// Static demo data, so the two lanes are derived once instead of on every render.
const EDIT_FILES = RAW.filter((f) => f.lane === "edit").map((f) => f.name);
const PRINT_FILES = RAW.filter((f) => f.lane === "print")
  .map((f) => `${f.name.replace(".CR3", "")}_${f.size}_203WS.CR3`);
const MASTER_SIZES = ["8x12in(SC)", "12x18in(SC)+F", "16x24in(SC)", "4x6in"];

/** Landing section: CSS-only animation of the two-stage sorter, gated by IntersectionObserver. */
export const SorterShowcase = () => {
  const { t } = useI18n();
  const ref = useRef(null);
  const [run, setRun] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setRun(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setRun(true)),
      { threshold: 0.35 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, []);

  return (
    <div ref={ref} data-testid="sorter-showcase">
      <p className="eyebrow mb-4">{t("sorter_eyebrow")}</p>
      <h2 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight max-w-2xl">{t("sorter_title")}</h2>
      <p className="mt-6 text-base sm:text-lg text-[#A8A8A8] max-w-2xl leading-relaxed">{t("sorter_body")}</p>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-6 items-start">
        <div className="border border-[#2A2A2A] bg-[#0B0B0B] p-6" data-testid="sorter-raw-column">
          <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#707070]">
            <HardDriveDownload size={13} className="inline mr-2 -mt-0.5" />{t("sorter_raw")}
          </p>
          <ul className="mt-6 space-y-2">
            {RAW.map((f, i) => (
              <li key={f.name}
                className={`font-mono-accent text-[11px] text-[#A8A8A8] border border-[#1E1E1E] px-3 py-2 bg-[#0F0F0F] ${
                  run ? "sorter-fly" : "opacity-0"
                }`}
                style={{ animationDelay: `${i * 0.22}s` }}>
                {f.name}
              </li>
            ))}
          </ul>
        </div>

        <div className="hidden lg:flex flex-col items-center pt-24 gap-2" aria-hidden="true">
          <span className={`block h-px w-16 bg-[#D4AF37] ${run ? "sorter-trace" : "opacity-0"}`} />
          <span className="font-mono-accent text-[9px] tracking-[0.2em] uppercase text-[#D4AF37]">phase 1</span>
          <span className={`block h-px w-16 bg-[#D4AF37] ${run ? "sorter-trace" : "opacity-0"}`}
            style={{ animationDelay: "0.4s" }} />
        </div>

        <div className="space-y-5">
          <Bucket run={run} delay={1.1} label={t("sorter_edit_folder")} path="Selected_17899_Anita/EDIT" testId="sorter-edit-bucket"
            files={EDIT_FILES} />
          <Bucket run={run} delay={1.5} label={t("sorter_print_folder")} path="Selected_17899_Anita/CETAK" testId="sorter-print-bucket"
            files={PRINT_FILES} />
        </div>
      </div>

      <div className="mt-8 border border-[#D4AF37]/30 bg-[#D4AF37]/[0.03] p-6" data-testid="sorter-phase2">
        <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">{t("sorter_phase2")}</p>
        <p className="mt-3 text-sm text-[#A8A8A8] max-w-2xl leading-relaxed">{t("sorter_showcase_phase2_body")}</p>
        <div className="mt-6 flex flex-wrap gap-3">
          {MASTER_SIZES.map((size, i) => (
            <span key={size} data-testid={`sorter-master-${size}`}
              className={`font-mono-accent text-[10px] tracking-[0.12em] border border-[#2A2A2A] bg-[#0B0B0B] px-3 py-2 text-[#F3F3F1] ${
                run ? "sorter-pop" : "opacity-0"
              }`}
              style={{ animationDelay: `${2 + i * 0.18}s` }}>
              MASTER_SIAP_CETAK/{size}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
};

const Bucket = ({ run, delay, label, path, files, testId }) => (
  <div className="border border-[#2A2A2A] bg-[#0B0B0B] p-6" data-testid={testId}>
    <p className="font-mono-accent text-[10px] tracking-[0.2em] uppercase text-[#D4AF37]">
      <FolderOpen size={13} className="inline mr-2 -mt-0.5" />{label}
    </p>
    <p className="mt-2 font-mono-accent text-[10px] text-[#5A5A5A] break-all">{path}</p>
    <ul className="mt-5 space-y-2">
      {files.map((name, i) => (
        <li key={name}
          className={`font-mono-accent text-[11px] text-[#F3F3F1] border-l border-[#D4AF37] pl-3 py-1 break-all ${
            run ? "sorter-land" : "opacity-0"
          }`}
          style={{ animationDelay: `${delay + i * 0.2}s` }}>
          {name}
        </li>
      ))}
    </ul>
  </div>
);
